/**
 * Reimagine (Norton ODS) CDN paths relative to the `/reimagine` folder.
 * Leading `/`, no file extension unless the asset is not an SVG (`VectorImage` appends `.svg`).
 *
 * Use with `utils.getCdnReimagineImagesPath`, `utils.getThemedCdnImagesPath`, or
 * `renderUtils.renderReimagineIcon({ iconName })`.
 */
export const REIMAGINE_IMAGES = {
  aiAssistant: '/ai-assistant',
  alertTriangle: '/alert-triangle',
  brandIcon: '/brand-icon',
  brandLogo: '/brand-logo',
  cardLock: '/card-lock',
  check: '/check',
  checklist: '/checklist',
  coffeeCup: '/coffee-cup',
  creditKeys: '/credit-keys',
  familyUpsellFamilyExposureIcon: '/familyUpsell/family-exposure-icon',
  familyUpsellFamilyIllustration: '/familyUpsell/family-illustration',
  familyUpsellFamilyStaticBanner: '/familyUpsell/family-static-banner',
  familyUpsellAddMemberPrompt: '/familyUpsell/add-member-prompt',
  folderShare: '/folder-share',
  genericWhiteSvg: '/generic-white.svg',
  headphones: '/headphones',
  home: '/home',
  icCreditAttention: '/ic_credit_attention',
  icDetailedAccount: '/ic_detailed_account',
  icDetailedCreditCard: '/ic_detailed_credit_card',
  icDetailedDateOfBirth: '/ic_detailed_date_of_birth',
  icDetailedFingerprintLock: '/ic_detailed_fingerprint_lock',
  icDetailedInsurance: '/ic_detailed_insurance',
  icDetailedPrivacyMonitor: '/ic_detailed_privacy_monitor',
  icDetailedPrivacyMonitorAssistant: '/ic_detailed_privacy_monitor_assistant',
  icDetailedSocialMedia: '/ic_detailed_social_media',
  icRestorationAttention: '/ic_restoration_attention',
  icSimpleAdd: '/ic_simple_add',
  icSimpleBankTransfer: '/ic_simple_bank_transfer',
  icSimpleBlock: '/ic_simple_block',
  icSimpleBrowser: '/ic_simple_browser',
  icSimpleCall: '/ic_simple_call',
  icSimpleCheckmark: '/ic_simple_checkmark',
  icSimpleChevronLeft: '/ic_simple_chevron_left',
  icSimpleChevronRight: '/ic_simple_chevron_right',
  icSimpleClose: '/ic_simple_close',
  icSimpleCreditCard: '/ic_simple_credit_card',
  icSimpleEmployerId: '/ic_simple_employer_id',
  icSimpleExpandLess: '/ic_simple_expand_less',
  icSimpleExpandMore: '/ic_simple_expand_more',
  icSimpleFiletypeDocument: '/ic_simple_filetype_document',
  icSimpleFlagEmpty: '/ic_simple_flag_empty',
  icSimpleGamerTag: '/ic_simple_gamer_tag',
  icSimpleHeart: '/ic_simple_heart',
  icSimpleHome: '/ic_simple_home',
  icSimpleInfo: '/ic_simple_info',
  icSimpleInstallment: '/ic_simple_installment',
  icSimpleLock: '/ic_simple_lock',
  icSimpleMail: '/ic_simple_mail',
  icSimpleMother: '/ic_simple_mother',
  icSimplePassport: '/ic_simple_passport',
  icSimplePinLocation: '/ic_simple_pin_location',
  icSimpleProductFamilySpace: '/ic_simple_product_family_space',
  icSimpleProfileDetails: '/ic_simple_profile_details',
  icSimpleReload: '/ic_simple_reload',
  icSimpleSavingsAccount: '/ic_simple_savings_account',
  icSimpleSearch: '/ic_simple_search',
  icSimpleSettings: '/ic_simple_settings',
  icSimpleStatusAttention: '/ic_simple_status_attention',
  icSimpleStatusAttentionTriangle: '/ic_simple_status_attention_triangle',
  icSimpleStatusCriticalTriangleFilled: '/ic_simple_status_critical_triangle_filled',
  icSimpleStatusOkFilled: '/ic_simple_status_ok_filled',
  icSimpleStatusTriangleFilled: '/ic_simple_status_triangle_filled',
  icSimpleTaxpayerId: '/ic_simple_taxpayer_id',
  icSimpleThumbDownWhite: '/ic_simple_thumb_down_white',
  icSimpleThumbUpWhite: '/ic_simple_thumb_up_white',
  icSimpleUnlock: '/ic_simple_unlock',
  icSimpleUtilityFreeze: '/ic_simple_utility_freeze',
  icSocialMediaAttention: '/ic_social_media_attention',
  icTransactions: '/ic_transactions',
  idBadge: '/id-badge',
  identityMonitoring: '/identity-monitoring',
  identitySupport: '/identity-support',
  identityVault: '/identity-vault',
  memberInvite: '/member-invite',
  messagesClose: '/messages-close',
  protectionAlerts: '/protection-alerts',
  protectionShield: '/protection-shield',
  ratingBadge: '/rating-badge',
  rebrandWelcomeJson: '/rebrand_welcome.json',
  renewalReminder: '/renewal-reminder',
  secureDownload: '/secure-download',
  supportMessages: '/support-messages',
  wirelessCharging: '/wireless-charging',
} as const;

export type ReimagineImagePath = (typeof REIMAGINE_IMAGES)[keyof typeof REIMAGINE_IMAGES];

/** Basename without leading `/` for callers that prepend it (Tooltip `imageName`, icon maps). */
export function reimagineImageName(path: ReimagineImagePath): string {
  return path.startsWith('/') ? path.slice(1) : path;
}
